import { hex } from "../shared/color";
import { BONE, SCALE, type SkinDraw } from "./rig";

/** Live accessory state. The sim writes this before the local stick is painted. */
export const heroFx = { grab: 0, charge: 0, magnet: 0, gem: 0, gx: 0, gy: 0 };

const LAMP = hex("#ffe26b");
const GOGGLE = hex("#7be8ff");
const HAT = hex("#e23d4a");
const NOSE = hex("#ff2d2d");

/** Riveted gauntlet, welding goggles, and a horseshoe while the magnet is out. */
const iron: SkinDraw = {
  id: "iron",
  head(pen) {
    const x = pen.rig.head.x / SCALE;
    const y = pen.rig.head.y / SCALE;
    pen.disc(x, y, 3.75, pen.near);
    pen.disc(x - 3.3, y + 0.15, 0.7, hex("#2c333d"));
    pen.disc(x + 3.3, y + 0.15, 0.7, hex("#2c333d"));
    pen.disc(x, y - 1.7, 0.55, hex("#4a5564"));
    pen.disc(x - 1.7, y + 0.35, 1.15, hex("#1a1422"));
    pen.disc(x + 1.7, y + 0.35, 1.15, hex("#1a1422"));
    pen.px(x - 2.1, y, hex("#7be8ff"), 1);
    pen.px(x + 1.3, y, hex("#7be8ff"), 1);
  },
  front(pen) {
    const hx = pen.rig.handN.x / SCALE;
    const hy = pen.rig.handN.y / SCALE;
    const ex = pen.rig.elbowN.x / SCALE;
    const ey = pen.rig.elbowN.y / SCALE;
    for (let i = 0; i <= 4; i++) {
      const t = 0.5 + i / 8;
      const x = ex + (hx - ex) * t;
      const y = ey + (hy - ey) * t;
      pen.disc(x, y, i === 4 ? 2.15 : 1.7, i % 2 ? hex("#2c333d") : hex("#3e4652"));
    }
    pen.disc(hx, hy, 2.05, hex("#4a5564"));
    pen.px(hx - 1, hy - 1, hex("#e7edf5"), 1);
    pen.px(hx + 1, hy + 1, hex("#e7edf5"), 1);
    if (heroFx.magnet <= 0) return;
    const ang = pen.rig.gunAngle;
    const c = Math.cos(ang);
    const s = Math.sin(ang);
    const stamp = (lx: number, ly: number, color: number) => {
      pen.px(hx + c * lx - s * ly, hy + s * lx + c * ly, color, 1);
    };
    const red = hex("#e23b3b");
    const shade = hex("#a11d1d");
    const dark = hex("#8e1c1c");
    const tip = hex("#f4f7fb");
    for (let x = 1; x <= 5; x++) {
      stamp(x, -2, x < 3 ? dark : red);
      stamp(x, 2, x < 3 ? shade : red);
    }
    stamp(0, -1, dark);
    stamp(0, 0, shade);
    stamp(0, 1, dark);
    stamp(1, -1, red);
    stamp(1, 0, shade);
    stamp(1, 1, red);
    stamp(5, -2, tip);
    stamp(6, -2, hex("#ffffff"));
    stamp(5, 2, tip);
    stamp(6, 2, hex("#ffffff"));
    stamp(7, -3, hex("#ffd0d0"));
    stamp(8, -2, hex("#ffffff"));
    stamp(7, 3, hex("#ffd0d0"));
    stamp(8, 2, hex("#ffffff"));
    stamp(6, -1, hex("#ffd0d0"));
    stamp(6, 1, hex("#ffd0d0"));
  },
  glow(pen) {
    if (heroFx.grab <= 0.2) return;
    const x = pen.rig.handN.x / SCALE;
    const y = pen.rig.handN.y / SCALE;
    const flick = Math.sin(pen.t * 42) > 0;
    const blue = hex("#3ec7ff");
    pen.px(x + 3, y - 3, flick ? hex("#ffffff") : blue, 2);
    pen.px(x + 4, y - 1, blue, 2);
    pen.px(x + 2, y - 4, flick ? blue : hex("#ffffff"), 2);
    pen.px(x - 2, y - 3, blue, 2);
    pen.glow(x, y, 7, blue, flick ? 0.55 : 0.28);
  },
};

/**
 * Yellow miner's helmet on the stickman head. A head callback hides the base disc,
 * so the orange head is drawn here first and the helmet only covers the crown.
 * Disc radii stay off the 1–1.4 band, which paints a plus and reads as a medic cross.
 */
const HELM = hex("#ffd84d");
const HELM_DARK = hex("#c4922a");
const HELM_DEEP = hex("#8a6418");

const digger: SkinDraw = {
  id: "digger",
  head(pen) {
    const x = pen.rig.head.x / SCALE;
    const y = pen.rig.head.y / SCALE;
    const face = pen.rig.now.facing || 1;
    pen.disc(x, y, BONE.headR / SCALE, pen.near);
    pen.disc(x, y - 2.2, 3.2, HELM);
    pen.disc(x, y - 3.15, 2.15, HELM_DARK);
    pen.disc(x + face * 2.15, y - 1.05, 1.6, HELM_DEEP);
    pen.disc(x + face * 2.9, y - 0.85, 0.8, HELM);
    pen.disc(x + face * 2.2, y - 2.2, 1.55, hex("#3a2c12"));
    pen.disc(x + face * 2.35, y - 2.2, 0.45, LAMP);
    pen.glow(x + face * 2.5, y - 2.2, 2.1, LAMP, 0.42);
    pen.disc(x - 1.05, y + 1.15, 0.85, hex("#1a1422"));
    pen.disc(x + 1.05, y + 1.15, 0.85, hex("#1a1422"));
    pen.disc(x - 1.05, y + 1.15, 0.42, GOGGLE);
    pen.disc(x + 1.05, y + 1.15, 0.42, GOGGLE);
  },
};

/** Hat on the crown, nose on the face, ruff at the neck. The base head stays. */
const clown: SkinDraw = {
  id: "clown",
  head(pen) {
    const x = pen.rig.head.x / SCALE;
    const y = pen.rig.head.y / SCALE;
    const f = pen.rig.now.facing || 1;
    const cream = hex("#fff6f2");
    const gold = hex("#ffd23f");
    const white = hex("#ffffff");
    const brim = hex("#a8222f");
    const hi = hex("#ff8a8a");
    const ink = hex("#1a1422");
    const neck = y + 3.15;
    const ruff = [cream, HAT, gold];
    for (let i = -3; i <= 3; i++) {
      pen.disc(x + i * 1.45, neck + (Math.abs(i) % 2 ? -0.35 : 0.45), 1.25, ruff[(i + 3) % 3]);
      pen.px(x + i * 1.45 - 0.2, neck - 0.55, white, 1);
    }
    // Sits 1.6px higher than the first cut so the eyes and brow show under the brim.
    const lift = 1.6;
    pen.disc(x, y - 4.6 - lift, 3.35, ink);
    pen.disc(x, y - 4.8 - lift, 2.85, brim);
    pen.px(x - 1.1, y - 5.15 - lift, hi, 1);
    for (let i = 0; i < 6; i++) {
      pen.disc(x, y - 5.45 - lift - i * 0.92, Math.max(0.6, 2.35 - i * 0.3), i % 2 ? brim : HAT);
      pen.px(x - 0.45, y - 5.7 - lift - i * 0.92, hi, 1);
    }
    pen.disc(x + f * 2.15, y + 0.65, 1.15, NOSE);
    pen.px(x + f * 1.85, y + 0.35, hi, 1);
  },
};


/** Soft brimmed violet hat with a mint crystal tip. Street mage, not a tall clown cone. */
const hexSkin: SkinDraw = {
  id: "hex",
  head(pen) {
    const x = pen.rig.head.x / SCALE;
    const y = pen.rig.head.y / SCALE;
    const violet = hex("#b98cff");
    const crown = hex("#6c4aad");
    const ink = hex("#1a1422");
    const mint = hex("#7be8ff");
    const f = pen.rig.now.facing || 1;
    const lilac = hex("#dcc4ff");
    // Low rounded crown, leaning back a touch so it reads as soft felt, not a cone.
    const cx = x - f * 0.35;
    pen.disc(cx, y - 4.5, 2.75, ink);
    pen.disc(cx, y - 4.55, 2.25, crown);
    pen.disc(cx - f * 0.3, y - 4.95, 1.55, violet);
    pen.px(cx - f * 0.9, y - 5.9, lilac, 1);
    // Wide flat brim, both ends drooping: the silhouette is a hat, not a point.
    for (let i = -5; i <= 5; i++) {
      const droop = Math.abs(i) >= 4 ? 0.55 : 0;
      pen.disc(x + i * 1.05, y - 2.85 + droop, 1.05, ink);
    }
    for (let i = -5; i <= 5; i++) {
      const droop = Math.abs(i) >= 4 ? 0.55 : 0;
      pen.disc(x + i * 1.05, y - 3.1 + droop, 0.62, Math.abs(i) <= 1 ? crown : violet);
    }
    // Hat band.
    for (let i = -2; i <= 2; i++) pen.px(cx + i, y - 3.85, crown, 1);
    // Small faceted crystal on top; idle pulse ~1.2s.
    const pulse = 0.35 + 0.35 * (0.5 + 0.5 * Math.sin(pen.t * ((Math.PI * 2) / 1.2)));
    pen.disc(cx, y - 7.35, 0.85, mint);
    pen.px(cx - 0.3, y - 7.6, hex("#ffffff"), 1);
    pen.glow(cx, y - 7.35, 1.6, mint, pulse * 0.55);
  },
  front(pen) {
    // Staff gem flare (cast and impact): a burst of uneven rays, re-rolled each frame.
    if (heroFx.magnet <= 0 || heroFx.gem <= 0.02) return;
    const k = heroFx.gem;
    const x = heroFx.gx / SCALE;
    const y = heroFx.gy / SCALE;
    const hot = hex("#fff1c4");
    const amber = hex("#ffb03a");
    const red = hex("#ff4a1a");
    const frame = (pen.t * 60) | 0;
    const rays = 7;
    for (let r = 0; r < rays; r++) {
      const jit = Math.sin(frame * 12.9898 + r * 78.233) * 43758.5453;
      const n = jit - Math.floor(jit);
      const a = (r / rays) * Math.PI * 2 + n * 0.6;
      const len = (1.5 + n * 3.5) * (0.4 + k);
      for (let l = 1; l <= len; l++) pen.px(x + Math.cos(a) * l, y + Math.sin(a) * l, l < 2 ? hot : l < 3.5 ? amber : red, 1);
    }
    pen.px(x, y, hot, 1);
    pen.glow(x, y, 4 + 6 * k, red, 0.75 * k);
    pen.glow(x, y, 2.5 + 2 * k, amber, 0.6 * k);
  },
};

/** Pale energy ring + goggle discs. Charged technician, not a blue comic Tesla. */
const novaSkin: SkinDraw = {
  id: "nova",
  head(pen) {
    const x = pen.rig.head.x / SCALE;
    const y = pen.rig.head.y / SCALE;
    const white = hex("#ffffff");
    const lamp = hex("#ffe26b");
    const ink = hex("#1a1422");
    // Thin pale halo (outer ring strokes; keep face disc visible).
    pen.disc(x, y - 0.15, 5.1, white);
    pen.disc(x, y - 0.15, 4.4, pen.near);
    pen.disc(x, y - 0.15, 4.9, lamp);
    pen.disc(x, y - 0.15, 4.2, pen.near);
    // Infrequent spark ticks (≤2Hz).
    if (Math.sin(pen.t * Math.PI * 2) > 0.82) {
      const a = (pen.t * 3.1) % (Math.PI * 2);
      pen.px(x + Math.cos(a) * 4.55, y - 0.15 + Math.sin(a) * 4.55, white, 1);
      pen.px(x + Math.cos(a + 2.2) * 4.35, y - 0.15 + Math.sin(a + 2.2) * 4.35, lamp, 1);
    }
    // Goggle discs — dark frame, white lens.
    pen.disc(x - 1.35, y + 0.55, 1.15, ink);
    pen.disc(x + 1.35, y + 0.55, 1.15, ink);
    pen.disc(x - 1.35, y + 0.55, 0.55, white);
    pen.disc(x + 1.35, y + 0.55, 0.55, white);
    pen.px(x - 1.55, y + 0.35, lamp, 1);
    pen.px(x + 1.15, y + 0.35, lamp, 1);
  },
  front(pen) {
    // Tiny coil canister silhouette only while Tesla is equipped.
    if (heroFx.magnet <= 0) return;
    const hx = pen.rig.handN.x / SCALE;
    const hy = pen.rig.handN.y / SCALE;
    const ang = pen.rig.gunAngle;
    const c = Math.cos(ang);
    const s = Math.sin(ang);
    const stamp = (lx: number, ly: number, color: number) => {
      pen.px(hx + c * lx - s * ly, hy + s * lx + c * ly, color, 1);
    };
    const pale = hex("#fff6d8");
    const ink = hex("#1a1422");
    stamp(2, 0, ink);
    stamp(3, 0, pale);
    stamp(4, 0, hex("#ffffff"));
    stamp(5, 0, pale);
    stamp(3, -1, ink);
    stamp(3, 1, ink);
    stamp(4, -1, hex("#ffe26b"));
    stamp(4, 1, hex("#ffe26b"));
  },
};

export const CHARACTERS: Record<string, SkinDraw> = { iron, digger, clown, hex: hexSkin, nova: novaSkin };

export function skinsFor(id: string): { wear: SkinDraw[]; near: number; far: number; outline: number; hideTorso: boolean; hideArms: boolean; hideLegs: boolean } {
  const skin = CHARACTERS[id];
  if (!skin) {
    return {
      wear: [],
      near: hex("#ff7a2e"),
      far: hex("#c24c17"),
      outline: hex("#1a1422"),
      hideTorso: false,
      hideArms: false,
      hideLegs: false,
    };
  }
  return {
    wear: [skin],
    near: skin.near ?? hex("#ff7a2e"),
    far: skin.far ?? hex("#c24c17"),
    outline: skin.outline ?? hex("#1a1422"),
    hideTorso: !!skin.hide?.torso,
    hideArms: !!skin.hide?.arms,
    hideLegs: !!skin.hide?.legs,
  };
}
